/**
 * AI 解析プロファイル統合エディタ用の **ライブプレビュー** ヘルパー。
 *
 * 役割:
 *  - `buildAiPayloadPreview`: M1 (設定) + M2 (カテゴリマスタ) + M3 (出力フォーマット) + 投稿項目 から
 *    「実際に AI へ送るデータそのもの」を全項目について生成する。
 *    プロンプト本文は **shared-types の buildPrompt** (= バックエンド analyzePost と同一コード) を呼ぶため、
 *    プレビューはバックエンドの送信内容と**完全一致**し、将来もドリフトしない (方式A)。
 *  - `checkProfileIntegrity`: admin 設定値と validators.ts ハードコード値 / mode 整合性 /
 *    fieldPath 不存在 を検証して warning として返す。
 *
 * 注: 実際の Gemini 呼出・画像取得・API キーはバックエンド (functions/src/ai/analyzePost.ts) 側。
 * 本ヘルパーは「何が送られるか」を表示するための pure 関数。
 */

import {
  AI_MODEL_GENERATION_LABELS,
  aiGenerationSupportsSampling,
  aiGenerationSupportsThinkingBudget,
  aiGenerationSupportsThinkingLevel,
  buildPrompt,
  withPostFieldMappings,
  formatOutputSchema,
  getModeLabel,
  normalizeAiOutputFormat,
  resolveAiModelGeneration,
  type AiAnalysisSettings,
  type AiCategoryMaster,
  type AiGenerationConfig,
  type AiOutputFormat,
  type AiOutputFormatSchema,
  type AiPromptInputMapping,
  type AiPromptSections,
  type AiTitleGenerationConfig,
  type AiValidatorRule,
  type DistrictMode,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { DEFAULT_MODEL_NAME } from './aiM1Subforms';

// ---------------------------------------------------------------------------
// validators.ts ハードコード値 (mode 別 expected labels / output keys)
// ---------------------------------------------------------------------------

/**
 * functions/src/ai/validators.ts が mode 別に hard-code している assessment ラベル。
 * admin が M3.assessment にここと違う値を入れると validators の集計が動かないため、
 * 不一致を warning として知らせる。
 */
export const VALIDATORS_HARDCODED_LABELS: Partial<
  Record<DistrictMode, { positiveLabel: string; negativeLabel: string }>
> = {
  bousai: { positiveLabel: 'ホッと', negativeLabel: 'ハッと' },
  'kids-bousai': { positiveLabel: 'ホッと', negativeLabel: 'ハッと' },
  walker: { positiveLabel: 'いいね！', negativeLabel: 'もったいない' },
  // ecw は集計を持たないので check 対象外
};

/**
 * @deprecated 【廃止予定 / legacy 向け整合性チェック】functions/src/ai/validators.ts
 * (@deprecated mode 専用バリデータ) が mode 別に hard-code している集計 output フィールド名。
 *
 * MD-3 モードドリブン化 (2026-07-24) 以降、この不一致警告が意味を持つのは
 * `legacyModeResults=true` の legacy 地区のみ — 新規地区の generic パイプラインは
 * M3.aggregateScores のキーをそのまま尊重するため、どんなキー名でも「ずれ」は発生しない。
 * 撤去条件: legacy 地区 0 件 (validators.ts 撤去と同時)。
 */
export const VALIDATORS_HARDCODED_AGG_KEYS: Partial<
  Record<DistrictMode, { positive: string; negative: string; overall?: string }>
> = {
  bousai: { positive: 'hotto_score', negative: 'hatto_score' },
  'kids-bousai': { positive: 'hotto_score', negative: 'hatto_score' },
  walker: { positive: 'iine_score', negative: 'mottainai_score', overall: 'walkable_score' },
};

// ---------------------------------------------------------------------------
// 1) AI 送信ペイロード プレビュー — 「実際に AI へ送るデータそのもの」を全項目について生成。
//    プロンプト本文 / 出力スキーマは shared-types の buildPrompt / formatOutputSchema
//    (= バックエンドと同一コード) を呼ぶため完全一致する。
// ---------------------------------------------------------------------------

export interface AiPayloadPreviewInput {
  mode: DistrictMode;
  /** legacy 一枚岩プロンプト (promptSections 未使用テンプレ用)。 */
  systemInstructions: string;
  /** セクション式プロンプト (地区モードでは実効マージ済みの値)。 */
  promptSections: AiPromptSections;
  promptInputMapping: AiPromptInputMapping[];
  /** 実効 inputFieldKeys (undefined=全送信 / []=送らない / 明示配列)。 */
  inputFieldKeys: string[] | undefined;
  generationConfig: AiGenerationConfig | null;
  validatorRules: AiValidatorRule[];
  titleGeneration: AiTitleGenerationConfig | null;
  /** 参照カテゴリマスタ (categoryMasterIds で選択済みの本体)。 */
  m2List: (AiCategoryMaster & { id: string })[];
  /** 出力フォーマットのスキーマ本体 (M3.schema、未選択なら undefined)。 */
  m3Schema: AiOutputFormatSchema | undefined;
  /**
   * 投稿項目定義 (テンプレモード=mode 既定、地区モード=その地区の dataSettings.postFields)。
   * 日本語ラベル表示 + サンプル投稿の組み立てに使う。
   */
  postFields: FieldDef[];
}

export interface AiPayloadPostDataRow {
  /** プロンプト変数名。 */
  templateVar: string;
  /** 投稿データの参照元パス (例: title / extraFields.health)。 */
  source: string;
  /** 日本語ラベル (postFields 由来、無ければ空)。 */
  label: string;
  /** inputFieldKeys フィルタ後に AI へ送られるか。 */
  sent: boolean;
}

export interface AiPayloadParamRow {
  label: string;
  value: string;
  /** 値未設定でランタイム既定が使われる場合 true。 */
  isDefault: boolean;
}

export interface AiPayloadPreview {
  /** buildPrompt が組み立てる system instruction 全文 (= 実際に送られるプロンプト)。 */
  systemPrompt: string;
  /** AI に送る投稿データ一覧 (変数・投稿項目・日本語名・送信有無)。 */
  postData: AiPayloadPostDataRow[];
  /** 画像送信の説明。 */
  imageNote: string;
  /** 出力フォーマット (AI が返す JSON の指示、formatOutputSchema)。 */
  outputSchema: string;
  /** 出力検証ルールの人間可読サマリ。 */
  validators: string[];
  /** タイトル自動生成設定。 */
  titleGeneration: { enabled: boolean; instructions: string };
  /** Gemini パラメータ (model / temperature / ...)。 */
  generationParams: AiPayloadParamRow[];
}

/** ネストパス (segs) に値をセット (サンプル投稿の組み立て用)。 */
function setPath(root: Record<string, unknown>, segs: string[], value: unknown): void {
  let cur = root;
  for (let i = 0; i < segs.length - 1; i += 1) {
    const k = segs[i]!;
    if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
    cur = cur[k] as Record<string, unknown>;
  }
  cur[segs[segs.length - 1]!] = value;
}

export function buildAiPayloadPreview(input: AiPayloadPreviewInput): AiPayloadPreview {
  const {
    systemInstructions,
    promptSections,
    promptInputMapping,
    inputFieldKeys,
    generationConfig,
    validatorRules,
    titleGeneration,
    m2List,
    m3Schema,
    postFields,
  } = input;

  const fieldByKey = new Map(postFields.map((f) => [f.key, f] as const));
  const labelFor = (key: string) => fieldByKey.get(key)?.label ?? '';
  // 実行時 (buildPrompt) と同じく、対応表に無い投稿設定の項目を自動で足した対応表で見せる。
  const inputMapping = withPostFieldMappings(promptInputMapping, postFields);

  // サンプル投稿 + 投稿データ行を組み立てる。
  // (実投稿は無いので、各項目に「（◯◯の例）」プレースホルダを入れて「何が送られるか」を可視化する)
  const allow = inputFieldKeys === undefined ? null : new Set(inputFieldKeys);
  const samplePost: Record<string, unknown> = {};
  const postData: AiPayloadPostDataRow[] = [];
  for (const m of inputMapping) {
    const firstSource = (m.source.split('|')[0] ?? '').trim();
    // 参照元の先頭の `post.` は投稿そのものを指す (実行時は { post } 起点で引く) ので外して組み立てる。
    const rawSegs = firstSource.split('.').filter(Boolean);
    const segs = rawSegs[0] === 'post' ? rawSegs.slice(1) : rawSegs;
    const lastSeg = segs[segs.length - 1] ?? m.templateVar;
    const sent = !allow || [m.templateVar, lastSeg].some((k) => allow.has(k));
    const label = labelFor(lastSeg) || labelFor(m.templateVar) || '';
    const field = fieldByKey.get(lastSeg) ?? fieldByKey.get(m.templateVar);
    const isScore =
      m.templateVar === 'happiness' ||
      m.templateVar === 'health' ||
      field?.type === 'rating-scale' ||
      field?.type === 'number';
    const firstOption = field?.options?.[0]?.value;
    const sampleVal: unknown = isScore
      ? 2
      : field?.type === 'checkbox'
        ? true
        : firstOption && (field?.type === 'select' || field?.type === 'multiselect')
          ? field.type === 'multiselect'
            ? [firstOption]
            : firstOption
          : `（${label || m.templateVar}の例）`;
    if (segs.length > 0) setPath(samplePost, segs, sampleVal);
    postData.push({ templateVar: m.templateVar, source: m.source, label, sent });
  }

  // promptSections を使うか legacy systemInstructions かをバックエンドの分岐に合わせて決める。
  // (空セクションのみ かつ legacy 本文も無い場合はセクション式扱い=厳格ルール+スキーマのみ)
  const anySection = Object.values(promptSections).some(
    (v) => typeof v === 'string' && v.trim().length > 0,
  );
  const useSections = anySection || !systemInstructions.trim();

  // buildPrompt は settings の promptSections / systemInstructions / promptInputMapping のみ参照するため
  // 最小 settings を組んで渡す (型は AiAnalysisSettings に cast)。
  const settings = {
    promptSections: useSections ? promptSections : undefined,
    systemInstructions: systemInstructions ?? '',
    promptInputMapping: inputMapping,
  } as unknown as AiAnalysisSettings;

  let systemPrompt = '';
  try {
    systemPrompt = buildPrompt({
      settings,
      post: samplePost,
      categoryMaster: null,
      categoryMasters: m2List,
      outputFormat: m3Schema ?? null,
      inputFieldKeys,
      // 投稿設定を渡すと【投稿データ】がラベル・型・補足説明・定義・スケール付きの
      // 詳細形式になる (バックエンドと同一の組立にするため必ず渡す)。
      postFields,
    });
  } catch (e) {
    systemPrompt = `(プレビュー生成エラー: ${(e as Error).message})`;
  }

  const outputSchema = m3Schema ? formatOutputSchema(m3Schema) : '(出力フォーマット M3 が未選択)';

  const validators = validatorRules.map((r) => {
    const rr = r as unknown as Record<string, unknown>;
    const parts: string[] = [`項目: ${rr.fieldPath || '(未指定)'} ／ 種類: ${rr.kind}`];
    if (Array.isArray(rr.allowedValues) && rr.allowedValues.length > 0) {
      parts.push(`許可値: ${(rr.allowedValues as unknown[]).join(', ')}`);
    }
    if (typeof rr.maxItems === 'number') parts.push(`最大件数: ${rr.maxItems}`);
    const lr = rr.lengthRange as { min?: number; max?: number } | undefined;
    if (lr && (lr.min != null || lr.max != null)) {
      parts.push(`文字数: ${lr.min ?? ''}〜${lr.max ?? ''}`);
    }
    if (rr.categoryMasterId) parts.push(`カテゴリマスタ: ${rr.categoryMasterId}`);
    if (typeof rr.selectionCount === 'number') {
      parts.push(
        `選択数: ${rr.selectionCount}${rr.selectionCountMode ? ` (${rr.selectionCountMode})` : ''}`,
      );
    }
    return parts.join(' ／ ');
  });

  // 世代 (v2/v3) で有効な項目が変わるので、使われない項目は表に並べない
  // (実行時にも送られないため。判定は functions と同じ shared-types の関数を使う)。
  const g: Partial<AiGenerationConfig> = generationConfig ?? {};
  const generation = resolveAiModelGeneration(g);
  const num = (v: unknown) => (typeof v === 'number' ? String(v) : '—');
  const generationParams: AiPayloadParamRow[] = [
    {
      label: '世代',
      value: g.generation
        ? AI_MODEL_GENERATION_LABELS[generation]
        : `${AI_MODEL_GENERATION_LABELS[generation]} (モデル名から判定)`,
      isDefault: false,
    },
    { label: 'モデル', value: g.model || DEFAULT_MODEL_NAME, isDefault: !g.model },
  ];
  if (aiGenerationSupportsThinkingLevel(generation)) {
    generationParams.push({
      label: '推論レベル (thinkingLevel)',
      value: g.thinkingConfig?.thinkingLevel || 'モデル既定',
      isDefault: !g.thinkingConfig?.thinkingLevel,
    });
  }
  if (aiGenerationSupportsThinkingBudget(generation)) {
    generationParams.push({
      label: '思考トークン予算 (thinkingBudget)',
      value:
        typeof g.thinkingConfig?.thinkingBudget === 'number'
          ? String(g.thinkingConfig.thinkingBudget)
          : 'モデル既定',
      isDefault: typeof g.thinkingConfig?.thinkingBudget !== 'number',
    });
  }
  if (aiGenerationSupportsSampling(generation)) {
    generationParams.push(
      { label: '温度 (temperature)', value: num(g.temperature), isDefault: g.temperature == null },
      { label: 'Top-P 値 (topP)', value: num(g.topP), isDefault: g.topP == null },
      { label: 'Top-K 値 (topK)', value: num(g.topK), isDefault: g.topK == null },
    );
  }
  generationParams.push(
    {
      label: '最大出力トークン数 (maxOutputTokens)',
      value: num(g.maxOutputTokens),
      isDefault: g.maxOutputTokens == null,
    },
    {
      label: '応答 MIME 型 (responseMimeType)',
      value: g.responseMimeType || 'application/json',
      isDefault: !g.responseMimeType,
    },
  );

  return {
    systemPrompt,
    postData,
    imageNote:
      '投稿に写真があれば画像として送信されます（サムネ優先・multimodal）。写真なしの投稿はテキストのみ送信。',
    outputSchema,
    validators,
    titleGeneration: {
      enabled: !!titleGeneration?.enabled,
      instructions: titleGeneration?.instructions ?? '',
    },
    generationParams,
  };
}

// ---------------------------------------------------------------------------
// 2) Integrity check — 設定不整合を warning として返す
// ---------------------------------------------------------------------------

export interface IntegrityWarning {
  level: 'error' | 'warn' | 'info';
  message: string;
  hint?: string;
}

export interface IntegrityInput {
  mode: DistrictMode;
  m1: {
    outputFormatId: string;
    categoryMasterIds: string[];
    validatorRules: AiValidatorRule[];
    promptInputMapping: AiPromptInputMapping[];
    systemInstructions: string;
  };
  m2List: (AiCategoryMaster & { id: string })[];
  m3: (AiOutputFormat & { id: string }) | undefined;
  /**
   * 当該 mode の ModeProfile (M10) が推奨マスタとして登録している id 群。
   *
   * mode キーは「ラベル/フィルタタグ」であり挙動を決めないため (モードドリブン化 2026-07-24)、
   * mode 文字列が一致しないだけでは誤りとは言えない。**登記簿である ModeProfile が
   * その組合せを推奨しているなら不一致警告を出さない**。未指定なら従来どおり mode 一致のみで判定。
   */
  recommendedMasterIds?: readonly string[];
  /**
   * mode キー → 表示名の解決関数 (ModeProfile.modeLabel 駆動)。
   * 警告文に内部キーを出さないために使う (「UI に内部識別子を出さない」全体ルール)。
   * 未指定なら組込モードの固定ラベルへ fallback する。
   */
  resolveModeLabel?: (mode: DistrictMode) => string;
}

export function checkProfileIntegrity(input: IntegrityInput): IntegrityWarning[] {
  const out: IntegrityWarning[] = [];
  const { mode, m1, m2List, m3 } = input;
  // ModeProfile (M10) が推奨している組合せは、mode キーが揃っていなくても意図どおりとみなす。
  const recommended = new Set(input.recommendedMasterIds ?? []);
  const labelOf = input.resolveModeLabel ?? ((m: DistrictMode) => getModeLabel(m));
  const isIntended = (masterId: string, masterMode: DistrictMode): boolean =>
    masterMode === mode || recommended.has(masterId);

  // 1. outputFormatId 必須
  if (!m1.outputFormatId.trim()) {
    out.push({ level: 'error', message: '出力フォーマットが選ばれていません' });
  } else if (!m3) {
    out.push({
      level: 'error',
      message: `指定された出力フォーマット「${m1.outputFormatId}」が見つかりません`,
    });
  } else if (!isIntended(m3.id, m3.mode)) {
    out.push({
      level: 'warn',
      message: `出力フォーマット「${m3.displayName || m3.id}」は「${labelOf(m3.mode)}」向けとして登録されており、この解析設定の「${labelOf(mode)}」と一致しません`,
      hint: 'モードは絞り込み用のラベルなので動作には影響しません。意図した組合せならこのままで問題ありません。',
    });
  }

  // 2. M2 紐付き
  if (m1.categoryMasterIds.length === 0) {
    out.push({
      level: 'info',
      message: '分類マスタが 1 つも選ばれていません',
      hint: 'AI に分類の語彙を渡したい場合は分類マスタを選んでください',
    });
  } else {
    for (const id of m1.categoryMasterIds) {
      const m2 = m2List.find((m) => m.id === id);
      if (!m2) {
        out.push({
          level: 'error',
          message: `指定された分類マスタ「${id}」が見つかりません`,
        });
      } else if (!isIntended(m2.id, m2.mode)) {
        out.push({
          level: 'warn',
          message: `分類マスタ「${m2.displayName || m2.id}」は「${labelOf(m2.mode)}」向けとして登録されており、この解析設定の「${labelOf(mode)}」と一致しません`,
          hint: 'モードは絞り込み用のラベルなので動作には影響しません。意図した組合せならこのままで問題ありません。',
        });
      }
    }
  }

  // 3. validators.ts ハードコード値との一致 (集計 mode 限定)
  // 【廃止予定 / legacy 向け】この警告群は legacyModeResults=true の既存地区でのみ意味を持つ。
  // 新規地区の generic パイプラインは M3 の assessment / aggregateScores をそのまま尊重する。
  if (m3) {
    const expectedLabels = VALIDATORS_HARDCODED_LABELS[mode];
    if (expectedLabels) {
      if (m3.schema.assessment.positiveLabel !== expectedLabels.positiveLabel) {
        out.push({
          level: 'warn',
          message: `M3.assessment.positiveLabel="${m3.schema.assessment.positiveLabel}" が旧 validators 既定値 "${expectedLabels.positiveLabel}" と不一致`,
          hint: 'リファクタ以前から存在する地区 (legacy) のみ関係します。新規地区は M3 の値がそのまま使われるため対応不要です。',
        });
      }
      if (m3.schema.assessment.negativeLabel !== expectedLabels.negativeLabel) {
        out.push({
          level: 'warn',
          message: `M3.assessment.negativeLabel="${m3.schema.assessment.negativeLabel}" が旧 validators 既定値 "${expectedLabels.negativeLabel}" と不一致`,
          hint: 'リファクタ以前から存在する地区 (legacy) のみ関係します。',
        });
      }
    }
    const expectedAgg = VALIDATORS_HARDCODED_AGG_KEYS[mode];
    if (expectedAgg) {
      if (
        m3.schema.aggregateScores.positiveKey &&
        m3.schema.aggregateScores.positiveKey !== expectedAgg.positive
      ) {
        out.push({
          level: 'warn',
          message: `M3.aggregateScores.positiveKey="${m3.schema.aggregateScores.positiveKey}" が旧 validators 既定値 "${expectedAgg.positive}" と不一致`,
          hint: 'リファクタ以前から存在する地区 (legacy) ではプロンプトと保存フィールドがずれます。新規地区は M3 のキーがそのまま使われるため対応不要です。',
        });
      }
      if (
        m3.schema.aggregateScores.negativeKey &&
        m3.schema.aggregateScores.negativeKey !== expectedAgg.negative
      ) {
        out.push({
          level: 'warn',
          message: `M3.aggregateScores.negativeKey="${m3.schema.aggregateScores.negativeKey}" が旧 validators 既定値 "${expectedAgg.negative}" と不一致`,
        });
      }
      if (
        expectedAgg.overall &&
        m3.schema.aggregateScores.overallKey &&
        m3.schema.aggregateScores.overallKey !== expectedAgg.overall
      ) {
        out.push({
          level: 'warn',
          message: `M3.aggregateScores.overallKey="${m3.schema.aggregateScores.overallKey}" が旧 validators 既定値 "${expectedAgg.overall}" と不一致`,
        });
      }
    }
  }

  // 4. validators.rules の fieldPath が M3 出力 + 既知 mode field 内に存在するか
  if (m3) {
    const knownFields = collectKnownFields(mode, m3);
    for (let i = 0; i < m1.validatorRules.length; i += 1) {
      const r = m1.validatorRules[i]!;
      if (!r.fieldPath) continue;
      if (!knownFields.has(r.fieldPath)) {
        out.push({
          level: 'warn',
          message: `validators.rules[${i}].fieldPath="${r.fieldPath}" が出力スキーマに見つかりません`,
          hint: '出力に存在しない fieldPath を指定したルールは単に無視されます (no-op)',
        });
      }
      if (r.kind === 'categorySelection' && r.requireReason && r.reasonFieldPath) {
        if (!knownFields.has(r.reasonFieldPath)) {
          out.push({
            level: 'warn',
            message: `validators.rules[${i}].reasonFieldPath="${r.reasonFieldPath}" が出力スキーマに見つかりません`,
          });
        }
      }
      if (r.kind === 'categorySelection' && r.categoryMasterId) {
        if (!m1.categoryMasterIds.includes(r.categoryMasterId)) {
          out.push({
            level: 'warn',
            message: `validators.rules[${i}].categoryMasterId="${r.categoryMasterId}" が M1.categoryMasterIds に含まれていません`,
          });
        }
      }
    }
  }

  // 5. promptInputMapping の templateVar が systemInstructions 内で参照されているか
  for (let i = 0; i < m1.promptInputMapping.length; i += 1) {
    const m = m1.promptInputMapping[i]!;
    if (!m.templateVar) continue;
    if (!m1.systemInstructions.includes(`{{${m.templateVar}}}`)) {
      out.push({
        level: 'info',
        message: `promptInputMapping[${i}] の {{${m.templateVar}}} が systemInstructions 内で使われていません`,
      });
    }
  }

  return out;
}

const MODE_KNOWN_FIELDS: Record<DistrictMode, string[]> = {
  ecw: [
    'emotions',
    'category_codes',
    'description',
    'emotion_text',
    'emotion_reason',
    'category_reason',
  ],
  bousai: ['classifications', 'description', 'professional_insight', 'reasoning'],
  walker: ['classifications', 'description', 'professional_insight'],
  'kids-bousai': ['classifications', 'description', 'professional_insight', 'reasoning'],
};

/**
 * validators.rules の fieldPath 検証に使う「存在しうる出力キー」集合。
 *
 * **正は M3 の実体** — `normalizeAiOutputFormat` が返す `fields[]` (明示 fields[] があれば
 * それ、無ければ classificationAxes 等からの派生) が出力列名の唯一の真実 (docs/105 レベル 3)。
 * mode 別の静的リスト (`MODE_KNOWN_FIELDS`) は legacy 地区向けの補完として最後に足すだけで、
 * custom mode でも M3 さえ揃っていれば誤警告は出ない。
 */
function collectKnownFields(mode: DistrictMode, m3: AiOutputFormat): Set<string> {
  const s = new Set<string>();
  const schema = normalizeAiOutputFormat(m3.schema);
  // fields[] (正) — object-array の itemFields はドット付きでも素のキーでも引けるようにする。
  for (const f of schema.fields ?? []) {
    s.add(f.key);
    if (f.kind === 'object-array') {
      for (const item of f.itemFields ?? []) {
        s.add(item.key);
        s.add(`${f.key}.${item.key}`);
      }
    }
  }
  if (schema.aggregateScores.positiveKey) s.add(schema.aggregateScores.positiveKey);
  if (schema.aggregateScores.negativeKey) s.add(schema.aggregateScores.negativeKey);
  if (schema.aggregateScores.overallKey) s.add(schema.aggregateScores.overallKey);
  if (schema.reasoningField) s.add(schema.reasoningField);
  for (const ax of schema.classificationAxes ?? []) {
    s.add(ax.codeField);
    s.add(ax.nameField);
    if (ax.subField) s.add(ax.subField);
  }
  // 【廃止予定 fallback】legacy 地区の mode 専用出力キー (撤去条件: legacy 地区 0 件、issue #45)。
  for (const k of MODE_KNOWN_FIELDS[mode] ?? []) s.add(k);
  return s;
}
